import { Helmet } from 'react-helmet-async';
import ContactUsComponent from '../../components/ContactUsComponent'

const deliverables = [
  "Completely rebuilt and re-engineered the JJ Eve platform",
  "Implemented automated event scheduling and reminders",
  "Developed a robust ticketing system for events",
  "Integrated real-time analytics for event performance tracking",
  "Created a user-friendly interface for event registration",
  "Implemented a secure payment gateway for ticket sales",
  "Developed mobile apps for event organizers and attendees",
  "Integrated social media sharing capabilities for events",
  "Set up the software development and business processes"
];

const JJEve = () => {
  return (
    <>
      <Helmet>
        <title>JJ Eve Event Management Software | Event Planning Platform by Data Repo</title>
        <meta
          name="description"
          content="JJ Eve by Data Repo is an all-in-one event management software that helps businesses and organizers plan events, manage guests, sell tickets, track schedules, and streamline event operations with ease."
        />
      </Helmet>

      <section
        style={{
          backgroundImage: `url(../images/bg-casestudies-box.png)`,
          backgroundSize: "100% 80vh"
        }}
        className="hero--container relative flex flex-col justify-center w-full h-[60vh] bg-no-repeat bg-fixed md:bg-origin-border md:h-[80vh] pt-[65px]">
        <h1 className="text-center text-white text-2xl md:text-5xl font-bold uppercase">JJ Eve</h1>
        <div className="row--image-style bg-transparent w-full absolute bottom-0 left-0 right-0">
          <svg className="waves w-full h-44" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 300" preserveAspectRatio="none">
            <path fill="rgb(226 232 240)" d="M 1000 280 l 2 -253 c -155 -36 -310 135 -415 164 c -102.64 28.35 -149 -32 -235 -31 c -80 1 -142 53 -229 80 c -65.54 20.34 -101 15 -126 11.61 v 54.39 z"></path>
            <path fill="rgb(100 116 139)" d="M 1000 261 l 2 -222 c -157 -43 -312 144 -405 178 c -101.11 33.38 -159 -47 -242 -46 c -80 1 -153.09 54.07 -229 87 c -65.21 25.59 -104.07 16.72 -126 16.61 v 22.39 z"></path>
            <path fill="#E5E7EB" d="M 1000 300 l 1 -230.29 c -217 -12.71 -300.47 129.15 -404 156.29 c -103 27 -174 -30 -257 -29 c -80 1 -130.09 37.07 -214 70 c -61.23 24 -108 15.61 -126 10.61 v 22.39 z"></path>
          </svg>
        </div>
      </section>

      <section className="w-full pt-10 px-8 bg-gradient-to-r from-blue-100 via-white to-blue-100 relative overflow-hidden">
        <div className="absolute -top-20 -left-20 w-72 h-72 bg-blue-200 rounded-full opacity-30 filter blur-3xl"></div>
        <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-purple-200 rounded-full opacity-20 filter blur-3xl"></div>

        <div className="flex flex-col md:flex-row items-center justify-center px-16 relative z-10">
          <div className="md:w-1/2 flex justify-center items-center p-5">
            <div className="bg-white rounded-xl shadow-2xl p-4 hover:scale-105 transform transition-transform duration-300">
              <img
                src="../images/jjeve-new.png"
                alt="car rental software dashboard"
                className="rounded-lg"
              />
            </div>
          </div>
          <div className="md:w-1/2 flex justify-center items-center p-5">
            <div className="bg-white rounded-xl shadow-2xl p-4 hover:scale-105 transform transition-transform duration-300">
              <img
                src="../images/jjeve-templates.png"
                alt="car rental software dashboard"
                className="rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="w-full items-center justify-center bg-gray-200">
        <div className="max-w-screen-lg mx-auto py-8">
          <div className="px-8 md:px-1 md:pr-2 py-6">
            <div className="mb-4 text-lg font-normal text-center md:text-left text-gray-600">
              <p>
                <strong>Industry:</strong> Event Management; Software Development
              </p>
              <p>
                <strong>Location:</strong> USA
              </p>
              <p>
                <strong>Areas of expertise:</strong> Back-end; Front-end; Quality assurance; Project management; AI
              </p>
              <p>
                <strong>Timeline:</strong> 2018-now
              </p>
            </div>

            <p className="text-xl font-normal text-center md:text-left text-gray-600">
              <strong>JJ Eve</strong> is a comprehensive event management solution designed to simplify the organization and execution of any event.
              Whether it’s a small community gathering or a large-scale corporate summit, JJ Eve provides all the tools you need to make your events seamless, professional, and impactful.
            </p>

            <div className="pt-8">
              <h2 className="text-black text-2xl md:text-3xl font-bold py-5">
                Experience the Power of JJ Eve
              </h2>
              <p className="text-xl font-normal text-center md:text-left text-gray-600">
                <strong>JJ Eve</strong> isn’t just an event platform — it’s your all-in-one command center for planning, managing, and celebrating events that leave a lasting impression.
                From conferences to concerts, JJ Eve gives you the control, clarity, and confidence to deliver exceptional experiences.
                <br /><br />
                <strong>See below how each feature helps you plan smarter, engage better, and manage effortlessly:</strong>
              </p>

              <div className="space-y-8 text-xl font-normal text-gray-600 pt-8">
                <div>
                  <h3 className="text-2xl font-semibold text-black pb-2">
                    1. Smart Event Creation and Scheduling
                  </h3>
                  <p>
                    Create, organize, and manage events with ease. JJ Eve’s scheduling tools let you plan venues, sessions, and guest timelines without missing a beat.
                    <br /><br />
                    <em>Save hours of manual coordination and keep every event running on time, every time.</em>
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-black pb-2">
                    2. Customizable Invitation Templates
                  </h3>
                  <p>
                    Wow your guests with elegant, ready-to-use invitation templates that include sections for <strong>Home</strong>, <strong>Gallery</strong>, <strong>About Us</strong>, <strong>RSVP</strong>, and <strong>Guest Info</strong>.
                    <br /><br />
                    <em>No design skills required — just customize your template and share your stunning event page in minutes.</em>
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-black pb-2">
                    3. Guest List Management
                  </h3>
                  <p>
                    Manage every guest effortlessly. JJ Eve tracks RSVPs, preferences, and seating plans in real-time so you stay organized and in control.
                    <br /><br />
                    <em>Keep your guests delighted and your team stress-free with real-time visibility into every attendee’s status.</em>
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-black pb-2">
                    4. Program and Agenda Builder
                  </h3>
                  <p>
                    Build and update event schedules in minutes. Add speakers, sessions, and timings, and share instantly with your audience.
                    Real-time updates ensure no one misses a change.
                    <br /><br />
                    <em>Ensure every session runs seamlessly and your event stays perfectly timed.</em>
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-black pb-2">
                    5. User and Role Management
                  </h3>
                  <p>
                    Keep your event team organized with secure role-based access. Assign permissions to managers, staff, or vendors and maintain full control over responsibilities.
                    <br /><br />
                    <em>Empower your team while protecting sensitive data — efficiency and security in one.</em>
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-semibold text-black pb-2">
                    6. Daily Notes and Activity Logging
                  </h3>
                  <p>
                    Keep track of your daily activities and event-related details effortlessly with JJ Eve.
                    The Daily Notes feature allows your team to record important updates, ideas, and observations in one convenient place.
                    <br /><br />
                    <em>Stay organized, maintain clarity, and ensure every event detail is documented for easy reference.</em>
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-8">
              <h2 className="text-black text-2xl md:text-3xl font-bold py-5">
                Deliverables
              </h2>
              <ul className="list-disc pl-5 text-xl font-normal text-center md:text-left text-gray-600">
                {deliverables.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="pt-8">
              <h2 className="text-black text-2xl md:text-3xl font-bold py-5">
                Project Results
              </h2>
              <p className="text-xl font-normal text-center md:text-left text-gray-600 pb-8">
                <strong>JJ Eve</strong> has transformed how events are planned, managed, and experienced. From streamlining operations to delivering unforgettable attendee experiences, this platform delivers proven results:
              </p>
              <ul className="list-disc pl-5 text-xl font-normal text-center md:text-left text-gray-600">
                <li>Reduced event planning and execution time significantly.</li>
                <li>Enhanced attendee engagement and satisfaction through seamless digital experiences.</li>
                <li>Empowered data-driven decisions with real-time performance analytics.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <ContactUsComponent />
    </>
  )
}

export default JJEve;